"use client";
/* eslint-disable import/no-extraneous-dependencies -- @stadium/ui omitted from services package.json to avoid a Turbo services↔ui cycle. */

import { ReactNode, useContext, useEffect } from "react";

import { useHubspotContext } from "@stadium/services/hubspot/tracking";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

const HideHubspotFeedbackForm = ({
  children,
  hideIcon = true,
}: {
  children?: ReactNode;
  hideIcon?: boolean;
}) => {
  const { isFeedbackLoaded } = useHubspotContext();
  const { isMobileDevice } = useContext(CurrentDeviceContext);

  const iframeClickListener = (feedbackElement: HTMLElement) => {
    const handleBannerClick = () => {
      setTimeout(() => {
        if (document.activeElement?.tagName === "IFRAME") {
          feedbackElement.style.setProperty("max-width", "100%", "important");
        }
      }, 100);
    };
    window.addEventListener("blur", handleBannerClick);
    return () => {
      window.removeEventListener("blur", handleBannerClick);
      feedbackElement.style.removeProperty("max-width");
    };
  };

  useEffect(() => {
    if (!isFeedbackLoaded) return;

    let timeout;
    let attempts = 0;
    let display = "block";

    const checkForElement = (): Promise<HTMLElement | null> => {
      return new Promise((resolve) => {
        const findElement = () => {
          const feedbackElement = document.getElementById("hs-feedback-ui");
          if (timeout) {
            clearTimeout(timeout);
          }

          if (feedbackElement) {
            resolve(feedbackElement);
            return;
          }

          if (attempts < 10) {
            attempts += 1;
            timeout = setTimeout(findElement, 1000);
          } else {
            resolve(null);
          }
        };

        findElement();
      });
    };

    const handleMobileFeedbackElement = async () => {
      const element = await checkForElement();
      if (!element) return;
      iframeClickListener(element);
    };

    const handleElement = async () => {
      const element = await checkForElement();
      if (!element) return;
      const defaultDisplay = element.style.getPropertyValue("display");
      display = defaultDisplay || display;
      element.style.setProperty("display", "none", "important");
    };

    if (!hideIcon) {
      if (isMobileDevice) {
        handleMobileFeedbackElement();
      }
      return;
    }

    handleElement();

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
      const feedbackElement = document.getElementById("hs-feedback-ui");
      if (feedbackElement) {
        feedbackElement.style.setProperty("display", display, "important");
      }
    };
  }, [isFeedbackLoaded, hideIcon, isMobileDevice]);

  return children;
};

export default HideHubspotFeedbackForm;
